import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  MapPin, Navigation, Plus, X, Play, 
  Loader2, RotateCcw, ChevronDown, ChevronUp, CheckCircle,
  Route as RouteIcon, Crosshair, MapPinPlus, Store, ArrowUp, ArrowDown, Map
} from 'lucide-react';
import { showToast } from './Toast';

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Nearest neighbor algorithm for route optimization
const optimizeRoute = (startLat, startLng, lojas) => {
  if (lojas.length === 0) return [];
  
  const remaining = [...lojas];
  const optimized = [];
  let currentLat = startLat;
  let currentLng = startLng;
  
  while (remaining.length > 0) {
    let nearestIdx = 0;
    let nearestDist = Infinity;
    
    remaining.forEach((loja, idx) => {
      if (loja.lat && loja.lng) {
        const dist = calculateDistance(currentLat, currentLng, loja.lat, loja.lng);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIdx = idx;
        }
      }
    });
    
    const nearest = remaining.splice(nearestIdx, 1)[0];
    optimized.push(nearest);
    if (nearest.lat && nearest.lng) {
      currentLat = nearest.lat;
      currentLng = nearest.lng;
    }
  }
  
  return optimized;
};

export function RotasTab({ lojas }) {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [selectedLojas, setSelectedLojas] = useState([]);
  const [showAVisitar, setShowAVisitar] = useState(true);
  const [showVisitadas, setShowVisitadas] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showMap, setShowMap] = useState(true);
  
  // Map refs
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const [L, setL] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);

  // Filter lojas by status (show all, but indicate GPS status)
  const lojasAVisitar = useMemo(() => 
    lojas.filter(l => l.status === 'a_visitar'),
    [lojas]
  );
  
  const lojasVisitadas = useMemo(() => 
    lojas.filter(l => !l.status || l.status === 'visitada'),
    [lojas]
  );

  // Check if loja has GPS
  const hasGPS = (loja) => loja.lat && loja.lng;

  // Load Leaflet
  useEffect(() => {
    if (typeof window !== 'undefined' && window.L) {
      setL(window.L);
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!L || !mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: false
    }).setView([42.408, -71.03], 10); // Default Boston area

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19
    }).addTo(map);

    mapRef.current = map;
    setIsMapReady(true);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setIsMapReady(false);
      }
    };
  }, [L]);

  // Update map markers - show ALL lojas, highlight selected ones
  useEffect(() => {
    if (!mapRef.current || !L || !isMapReady) return;

    // Clear existing markers and polyline
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.remove();
      polylineRef.current = null;
    }

    const routePoints = [];
    const allPoints = [];

    // Add current location marker if available
    if (currentLocation) {
      const startIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background:#22c55e;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);">📍</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      });

      const marker = L.marker([currentLocation.lat, currentLocation.lng], { icon: startIcon })
        .addTo(mapRef.current)
        .bindPopup('Your Location');
      
      markersRef.current.push(marker);
      routePoints.push([currentLocation.lat, currentLocation.lng]);
    }

    // Get all lojas with GPS
    const allLojas = lojas.filter(l => l.lat && l.lng);

    // Add markers for ALL lojas
    allLojas.forEach((loja) => {
      const isSelected = selectedLojas.some(s => s.id === loja.id);
      const selectedIndex = selectedLojas.findIndex(s => s.id === loja.id);
      const isAVisitar = loja.status === 'a_visitar';
      const storeNum = loja.numero || (lojas.findIndex(l => l.id === loja.id) + 1);
      
      let iconHtml;
      if (isSelected) {
        // Selected store - show route order number with blue background
        iconHtml = `<div style="background:#3b82f6;color:white;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);cursor:pointer;">${selectedIndex + 1}</div>`;
      } else if (isAVisitar) {
        // A Visitar store - red, clickable
        iconHtml = `<div style="background:#ef4444;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);cursor:pointer;opacity:0.9;">${storeNum < 10 ? '0' + storeNum : storeNum}</div>`;
      } else {
        // Visited store - cyan/blue, clickable
        iconHtml = `<div style="background:#0288d1;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);cursor:pointer;opacity:0.7;">${storeNum < 10 ? '0' + storeNum : storeNum}</div>`;
      }

      const icon = L.divIcon({
        className: 'custom-div-icon',
        html: iconHtml,
        iconSize: [isSelected ? 36 : 32, isSelected ? 36 : 32],
        iconAnchor: [isSelected ? 18 : 16, isSelected ? 18 : 16]
      });

      const statusLabel = isAVisitar ? '<span style="color:#ef4444;font-weight:bold;">TO VISIT</span>' : '<span style="color:#22c55e;font-weight:bold;">VISITED</span>';
      const addRemoveBtn = isSelected 
        ? `<br/><button onclick="window.removeFromRoute('${loja.id}')" style="background:#ef4444;color:white;border:none;padding:4px 8px;border-radius:4px;margin-top:6px;cursor:pointer;font-size:11px;">Remove from Route</button>`
        : `<br/><button onclick="window.addToRoute('${loja.id}')" style="background:#22c55e;color:white;border:none;padding:4px 8px;border-radius:4px;margin-top:6px;cursor:pointer;font-size:11px;">Add to Route</button>`;

      const marker = L.marker([loja.lat, loja.lng], { icon })
        .addTo(mapRef.current)
        .bindPopup(`<b>#${storeNum < 10 ? '0' + storeNum : storeNum} ${loja.name}</b><br/>${loja.city}, ${loja.state}<br/>${statusLabel}${addRemoveBtn}`);

      // Click handler to toggle selection
      marker.on('click', () => {
        // Don't toggle on click - let popup handle it
      });

      markersRef.current.push(marker);
      allPoints.push([loja.lat, loja.lng]);

      // Add to route points if selected
      if (isSelected) {
        // Insert at correct position based on selectedIndex
        routePoints.push({ lat: loja.lat, lng: loja.lng, index: selectedIndex });
      }
    });

    // Sort route points by selection order and extract coordinates
    const sortedRoutePoints = routePoints
      .filter(p => p.lat !== undefined || (p[0] !== undefined && p[1] !== undefined))
      .map(p => p.lat !== undefined ? [p.lat, p.lng] : p);

    // Rebuild route points in correct order
    const orderedRoutePoints = [];
    if (currentLocation) {
      orderedRoutePoints.push([currentLocation.lat, currentLocation.lng]);
    }
    selectedLojas.forEach(loja => {
      if (loja.lat && loja.lng) {
        orderedRoutePoints.push([loja.lat, loja.lng]);
      }
    });

    // Draw polyline connecting route points
    if (orderedRoutePoints.length > 1) {
      polylineRef.current = L.polyline(orderedRoutePoints, {
        color: '#3b82f6',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 10'
      }).addTo(mapRef.current);
    }

    // Fit map to show all lojas
    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      if (currentLocation) {
        bounds.extend([currentLocation.lat, currentLocation.lng]);
      }
      mapRef.current.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [lojas, selectedLojas, currentLocation, L, isMapReady]);

  // Global functions for popup buttons
  useEffect(() => {
    window.addToRoute = (lojaId) => {
      const loja = lojas.find(l => l.id === lojaId);
      if (loja && !selectedLojas.some(s => s.id === lojaId)) {
        setSelectedLojas(prev => [...prev, loja]);
        showToast(`${loja.name} added to route`);
        // Close popup
        if (mapRef.current) {
          mapRef.current.closePopup();
        }
      }
    };

    window.removeFromRoute = (lojaId) => {
      const loja = lojas.find(l => l.id === lojaId);
      setSelectedLojas(prev => prev.filter(s => s.id !== lojaId));
      if (loja) {
        showToast(`${loja.name} removed from route`);
      }
      // Close popup
      if (mapRef.current) {
        mapRef.current.closePopup();
      }
    };

    return () => {
      delete window.addToRoute;
      delete window.removeFromRoute;
    };
  }, [lojas, selectedLojas]);

  // Get current GPS location
  const handleGetLocation = useCallback(() => {
    if (!navigator.geolocation) {
      showToast('GPS não disponível neste dispositivo', 'error');
      return;
    }

    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        showToast('Localização obtida!');
        setGpsLoading(false);
      },
      (error) => {
        let message = 'Erro ao obter localização';
        if (error.code === error.PERMISSION_DENIED) {
          message = 'Permissão de localização negada';
        }
        showToast(message, 'error');
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Add loja to route
  const handleAddLoja = useCallback((loja) => {
    if (!hasGPS(loja)) {
      showToast('This store has no GPS coordinates', 'error');
      return;
    }
    if (selectedLojas.find(l => l.id === loja.id)) {
      showToast('Store already in route', 'warning');
      return;
    }
    setSelectedLojas(prev => [...prev, loja]);
    showToast(`${loja.name} added to route`);
  }, [selectedLojas]);

  // Remove loja from route
  const handleRemoveLoja = useCallback((lojaId) => {
    setSelectedLojas(prev => prev.filter(l => l.id !== lojaId));
  }, []);

  // Clear all selected
  const handleClearRoute = useCallback(() => {
    if (selectedLojas.length === 0) return;
    if (window.confirm('Clear entire route?')) {
      setSelectedLojas([]);
      showToast('Route cleared');
    }
  }, [selectedLojas.length]);

  // Optimize route order
  const handleOptimizeRoute = useCallback(() => {
    if (!currentLocation) {
      showToast('Get your location first', 'warning');
      return;
    }
    if (selectedLojas.length < 2) {
      showToast('Add at least 2 stores to optimize', 'warning');
      return;
    }

    const optimized = optimizeRoute(currentLocation.lat, currentLocation.lng, selectedLojas);
    setSelectedLojas(optimized);
    showToast('Route optimized!');
  }, [currentLocation, selectedLojas]);

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newList = [...selectedLojas];
    const [draggedItem] = newList.splice(draggedIndex, 1);
    newList.splice(index, 0, draggedItem);
    setSelectedLojas(newList);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Move item up in the route list
  const handleMoveUp = useCallback((index) => {
    if (index <= 0) return;
    const newList = [...selectedLojas];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setSelectedLojas(newList);
  }, [selectedLojas]);

  // Move item down in the route list
  const handleMoveDown = useCallback((index) => {
    if (index >= selectedLojas.length - 1) return;
    const newList = [...selectedLojas];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setSelectedLojas(newList);
  }, [selectedLojas]);

  // Open full route in Google Maps
  const handleOpenGoogleMaps = useCallback(() => {
    if (selectedLojas.length === 0) {
      showToast('Adicione lojas à rota primeiro', 'warning');
      return;
    }

    let url = 'https://www.google.com/maps/dir/?api=1';
    
    // Origin: current location or first store
    if (currentLocation) {
      url += `&origin=${currentLocation.lat},${currentLocation.lng}`;
    } else {
      url += `&origin=${selectedLojas[0].lat},${selectedLojas[0].lng}`;
    }

    // Destination: last store
    const lastLoja = selectedLojas[selectedLojas.length - 1];
    url += `&destination=${lastLoja.lat},${lastLoja.lng}`;

    // Waypoints: all stores except the last one (if we have origin from location)
    // or all stores except first and last (if first is origin)
    const waypointLojas = currentLocation 
      ? selectedLojas.slice(0, -1) 
      : selectedLojas.slice(1, -1);
    
    if (waypointLojas.length > 0) {
      const waypoints = waypointLojas.map(l => `${l.lat},${l.lng}`).join('|');
      url += `&waypoints=${waypoints}`;
    }

    url += '&travelmode=driving';
    window.open(url, '_blank');
  }, [currentLocation, selectedLojas]);

  // Open next store in Waze (store by store)
  const handleOpenWaze = useCallback((loja) => {
    if (!loja.lat || !loja.lng) {
      showToast('Esta loja não tem coordenadas GPS', 'error');
      return;
    }
    const url = `https://waze.com/ul?ll=${loja.lat},${loja.lng}&navigate=yes`;
    window.open(url, '_blank');
  }, []);

  // Calculate total distance
  const totalDistance = useMemo(() => {
    if (selectedLojas.length === 0) return 0;
    
    let total = 0;
    let prevLat = currentLocation?.lat || selectedLojas[0]?.lat;
    let prevLng = currentLocation?.lng || selectedLojas[0]?.lng;
    
    const startIdx = currentLocation ? 0 : 1;
    
    for (let i = startIdx; i < selectedLojas.length; i++) {
      const loja = selectedLojas[i];
      if (loja.lat && loja.lng && prevLat && prevLng) {
        total += calculateDistance(prevLat, prevLng, loja.lat, loja.lng);
        prevLat = loja.lat;
        prevLng = loja.lng;
      }
    }
    
    return total;
  }, [currentLocation, selectedLojas]);

  const isLojaSelected = useCallback((lojaId) => {
    return selectedLojas.some(l => l.id === lojaId);
  }, [selectedLojas]);

  return (
    <div className="flex flex-col gap-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
          <RouteIcon size={24} className="text-[var(--accent-primary)]" />
          Route Planner
        </h2>
      </div>

      {/* Interactive Map - Full Width */}
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] overflow-hidden">
        <div className="p-3 border-b border-[var(--border-color)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Map size={18} className="text-[var(--accent-primary)]" />
            <span className="text-sm font-medium text-[var(--text-primary)]">Click on stores to add to route</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-red-500"></span> To Visit
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-[#0288d1]"></span> Visited
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span> In Route
            </span>
          </div>
        </div>
        <div 
          ref={mapContainerRef}
          className="w-full h-[350px] bg-[var(--bg-secondary)]"
          data-testid="route-map"
        />
      </div>

      {/* Current Location */}
      <div className="bg-[var(--bg-card)] rounded-xl p-4 border border-[var(--border-color)]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-[var(--text-primary)]">
            📍 Starting Point
          </span>
          {currentLocation && (
            <span className="text-xs text-green-500 bg-green-500/10 px-2 py-1 rounded-full">
              Location obtained
            </span>
          )}
        </div>
        
        <button
          data-testid="btn-get-location"
          onClick={handleGetLocation}
          disabled={gpsLoading}
          className="w-full h-11 bg-purple-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-purple-700 font-medium text-sm disabled:opacity-50"
        >
          {gpsLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Getting location...
            </>
          ) : (
            <>
              <Crosshair size={18} /> 
              {currentLocation ? 'Update Location' : 'Get My Location'}
            </>
          )}
        </button>
        
        {currentLocation && (
          <p className="text-xs text-[var(--text-muted)] mt-2 text-center">
            {currentLocation.lat.toFixed(5)}, {currentLocation.lng.toFixed(5)}
          </p>
        )}
      </div>

      {/* Selected Route */}
      <div className="bg-[var(--bg-card)] rounded-xl p-4 border border-[var(--border-color)]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-[var(--text-primary)]">
            🗺️ Sua Rota ({selectedLojas.length} {selectedLojas.length === 1 ? 'loja' : 'lojas'})
          </span>
          {selectedLojas.length > 0 && (
            <button
              onClick={handleClearRoute}
              className="text-xs text-red-500 hover:text-red-600"
            >
              Limpar
            </button>
          )}
        </div>

        {selectedLojas.length === 0 ? (
          <div className="text-center py-6 text-[var(--text-muted)]">
            <RouteIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhuma loja na rota</p>
            <p className="text-xs mt-1">Selecione lojas abaixo para criar sua rota</p>
          </div>
        ) : (
          <>
            {/* Route List */}
            <div className="flex flex-col gap-2 mb-4">
              {selectedLojas.map((loja, index) => (
                <div
                  key={loja.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  data-testid={`route-item-${loja.id}`}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    draggedIndex === index 
                      ? 'bg-[var(--accent-primary)]/10 border-[var(--accent-primary)]' 
                      : 'bg-[var(--bg-chip)] border-[var(--border-color)]'
                  }`}
                >
                  {/* Move Up/Down buttons for touch devices */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="p-1 rounded text-[var(--text-muted)] hover:bg-[var(--bg-chip)] disabled:opacity-30"
                      title="Mover para cima"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === selectedLojas.length - 1}
                      className="p-1 rounded text-[var(--text-muted)] hover:bg-[var(--bg-chip)] disabled:opacity-30"
                      title="Mover para baixo"
                    >
                      <ArrowDown size={12} />
                    </button>
                  </div>
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[var(--accent-primary)] text-white text-xs font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                      {loja.name}
                    </p>
                    <p className="text-xs text-[var(--text-muted)] truncate">
                      {loja.city}, {loja.state}
                    </p>
                  </div>
                  <button
                    onClick={() => handleOpenWaze(loja)}
                    className="p-2 rounded-md text-blue-500 bg-blue-500/10 hover:bg-blue-500/20 flex-shrink-0"
                    title="Ir com Waze"
                  >
                    <Navigation size={14} />
                  </button>
                  <button
                    onClick={() => handleRemoveLoja(loja.id)}
                    className="p-2 rounded-md text-red-500 bg-red-500/10 hover:bg-red-500/20 flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            {/* Distance Info */}
            <div className="text-center text-sm text-[var(--text-secondary)] mb-4">
              Distância total estimada: <span className="font-bold">{totalDistance.toFixed(1)} km</span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <button
                data-testid="btn-optimize-route"
                onClick={handleOptimizeRoute}
                disabled={selectedLojas.length < 2 || !currentLocation}
                className="w-full h-10 bg-amber-500 text-white rounded-lg flex items-center justify-center gap-2 active:bg-amber-600 font-medium text-sm disabled:opacity-50"
              >
                <RotateCcw size={16} /> Otimizar Rota (Mais Próxima)
              </button>
              
              <button
                data-testid="btn-open-google-maps-route"
                onClick={handleOpenGoogleMaps}
                className="w-full h-12 bg-[#4285F4] text-white rounded-lg flex items-center justify-center gap-2 active:bg-[#3367D6] font-medium"
              >
                <Play size={18} /> Iniciar Rota no Google Maps
              </button>
            </div>
          </>
        )}
      </div>

      {/* Available Stores - A Visitar */}
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] overflow-hidden">
        <button
          onClick={() => setShowAVisitar(!showAVisitar)}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <span className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
            <MapPinPlus size={16} className="text-red-500" />
            Stores to Visit ({lojasAVisitar.length})
          </span>
          {showAVisitar ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        
        {showAVisitar && (
          <div className="px-4 pb-4">
            {lojasAVisitar.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] text-center py-4">
                No stores to visit registered
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {lojasAVisitar.map((loja) => (
                  <div
                    key={loja.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isLojaSelected(loja.id)
                        ? 'bg-green-500/10 border-green-500/30'
                        : !hasGPS(loja)
                        ? 'bg-[var(--bg-chip)] border-[var(--border-color)] opacity-60'
                        : 'bg-[var(--bg-chip)] border-[var(--border-color)]'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                        {loja.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-[var(--text-muted)]">
                          {loja.city}, {loja.state}
                        </p>
                        {!hasGPS(loja) && (
                          <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                            Sem GPS
                          </span>
                        )}
                      </div>
                    </div>
                    {isLojaSelected(loja.id) ? (
                      <span className="flex items-center gap-1 text-xs text-green-500 px-2 py-1 rounded-full bg-green-500/10">
                        <CheckCircle size={12} /> Na rota
                      </span>
                    ) : (
                      <button
                        data-testid={`btn-add-to-route-${loja.id}`}
                        onClick={() => handleAddLoja(loja)}
                        disabled={!hasGPS(loja)}
                        className={`p-2 rounded-md ${
                          hasGPS(loja) 
                            ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20' 
                            : 'text-gray-400 bg-gray-500/10 cursor-not-allowed'
                        }`}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Available Stores - Visitadas */}
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] overflow-hidden">
        <button
          onClick={() => setShowVisitadas(!showVisitadas)}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <span className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
            <Store size={16} className="text-[var(--accent-primary)]" />
            Lojas Visitadas ({lojasVisitadas.length})
          </span>
          {showVisitadas ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        
        {showVisitadas && (
          <div className="px-4 pb-4">
            {lojasVisitadas.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] text-center py-4">
                Nenhuma loja visitada cadastrada
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {lojasVisitadas.map((loja) => (
                  <div
                    key={loja.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isLojaSelected(loja.id)
                        ? 'bg-green-500/10 border-green-500/30'
                        : !hasGPS(loja)
                        ? 'bg-[var(--bg-chip)] border-[var(--border-color)] opacity-60'
                        : 'bg-[var(--bg-chip)] border-[var(--border-color)]'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                        {loja.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-[var(--text-muted)]">
                          {loja.city}, {loja.state}
                        </p>
                        {!hasGPS(loja) && (
                          <span className="text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">
                            Sem GPS
                          </span>
                        )}
                      </div>
                    </div>
                    {isLojaSelected(loja.id) ? (
                      <span className="flex items-center gap-1 text-xs text-green-500 px-2 py-1 rounded-full bg-green-500/10">
                        <CheckCircle size={12} /> Na rota
                      </span>
                    ) : (
                      <button
                        data-testid={`btn-add-to-route-${loja.id}`}
                        onClick={() => handleAddLoja(loja)}
                        disabled={!hasGPS(loja)}
                        className={`p-2 rounded-md ${
                          hasGPS(loja) 
                            ? 'text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 hover:bg-[var(--accent-primary)]/20' 
                            : 'text-gray-400 bg-gray-500/10 cursor-not-allowed'
                        }`}
                      >
                        <Plus size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
        <h4 className="text-sm font-semibold text-blue-500 mb-2">💡 Como usar</h4>
        <ul className="text-xs text-[var(--text-secondary)] space-y-1">
          <li>1. Obtenha sua localização atual</li>
          <li>2. Adicione lojas à rota clicando no <Plus size={12} className="inline" /></li>
          <li>3. Use <ArrowUp size={12} className="inline" /><ArrowDown size={12} className="inline" /> para reordenar ou &quot;Otimizar Rota&quot;</li>
          <li>4. Clique em <Navigation size={12} className="inline" /> para ir com Waze (loja por loja)</li>
          <li>5. Ou inicie a rota completa no Google Maps</li>
        </ul>
      </div>
    </div>
  );
}
